import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, WritableSignal } from '@angular/core';
import {
  Application,
  ApplicationCounts,
  ApplicationSearchType,
  ApplicationStatus,
  ApplicationStep,
  ApplicationUpdatePayload,
  BatchError,
  getApplicationFullName,
} from '@mkp/application/models';
import { CreateApplicationPayload } from '@mkp/application/ui';
import { getOptionalContext } from '@mkp/core/feature-interceptor';
import { BaseHttpResource, escapeDelimiters, QueryParams } from '@mkp/shared/data-access';
import { CoreListEnvelop } from '@mkp/shared/models';
import { ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment';
import { ApplicationCount } from '@mkp/talent/state/actions';
import { catchError, finalize, forkJoin, map, Observable, of } from 'rxjs';
import { ApplicationDto } from './application.dto';
import { mapApplicationDtoToModel } from './application.mapper';

interface FetchApplicationPayload {
  statusId: string | undefined;
  vacancyId: string;
  offset: number;
  limit: number;
  accountId: string;
}

interface FetchApplicationCountPayload {
  statusId: string;
  vacancyId: string;
  accountId: string;
}

interface FetchApplicationCountsPayload {
  statuses: ApplicationStatus[];
  vacancyId: string;
  accountId: string;
}

interface UpdateCallsPayload {
  updatePayloads: ApplicationUpdatePayload[];
  statusId: string;
  currentCount?: WritableSignal<number>;
}

interface DeleteCallsPayload {
  applications: Application[];
  currentCount?: WritableSignal<number>;
}

interface SearchApplicationsPayload {
  vacancyId: string;
  accountId: string;
  type: ApplicationSearchType;
  text: string;
}

const SEARCH_LIMIT = 10;

const headers = new HttpHeaders({
  maxRetry: 1,
});

@Injectable({
  providedIn: 'root',
})
export class ApplicationResource extends BaseHttpResource<
  ApplicationDto,
  CoreListEnvelop<ApplicationDto>,
  CreateApplicationPayload
> {
  protected override readonly apiUrl = inject(ENVIRONMENT_PROVIDER).api.ats;

  constructor() {
    super('application');
  }

  private getApplicationUrl(uri: string): string {
    return `${this.apiUrl}/${uri}`;
  }

  fetchApplicationsByStatus({
    statusId,
    vacancyId,
    offset,
    limit,
    accountId,
  }: FetchApplicationPayload): Observable<Application[]> {
    const params: QueryParams = {
      filter: getFilter({ statusId, vacancyId }),
      offset,
      limit,
    };

    return this.http
      .get<CoreListEnvelop<ApplicationDto>>(
        this.getApplicationUrl(`account/${accountId}/${this.uri}`),
        { params }
      )
      .pipe(
        map(({ _embedded: { results: applicationDtos } }) =>
          applicationDtos.map(mapApplicationDtoToModel)
        )
      );
  }

  search({ vacancyId, accountId, type, text }: SearchApplicationsPayload) {
    const params: QueryParams = {
      filter: getSearchFilter({ vacancyId, type, text }),
      limit: SEARCH_LIMIT,
    };

    return this.http
      .get<CoreListEnvelop<ApplicationDto>>(
        this.getApplicationUrl(`account/${accountId}/${this.uri}`),
        { params }
      )
      .pipe(
        map(({ _embedded: { results: applicationDtos }, totalCount }) => ({
          results: applicationDtos.map(mapApplicationDtoToModel),
          totalCount,
        }))
      );
  }

  getByIds(ids: string[]) {
    return this.getWithQuery({ filter: getFilterByIds(ids), limit: ids.length }).pipe(
      map(({ _embedded: { results: applicationDtos } }) =>
        applicationDtos.map(mapApplicationDtoToModel)
      )
    );
  }

  fetchApplicationCounts({
    statuses,
    vacancyId,
    accountId,
  }: FetchApplicationCountsPayload): Observable<ApplicationCounts> {
    return forkJoin(
      statuses.reduce(
        (acc, { id: statusId, step }) => ({
          ...acc,
          [step]: this.fetchApplicationCountByStatus({ statusId, vacancyId, accountId }),
        }),
        {} as Record<ApplicationStep, Observable<number>>
      )
    );
  }

  getApplicationCountPerVacancy(
    vacancyIds: string[],
    accountId: string
  ): Observable<ApplicationCount[]> {
    return forkJoin(
      vacancyIds.map((vacancyId) =>
        this.getApplicationCount(vacancyId, accountId).pipe(
          map((count) => ({ id: vacancyId, applicationCount: count }))
        )
      )
    );
  }

  getUpdateCalls({
    updatePayloads,
    statusId,
    currentCount,
  }: UpdateCallsPayload): Observable<Application | BatchError>[] {
    return updatePayloads.map(({ applicationId, applicationFullName, _version }) =>
      this.http
        .patch<ApplicationDto>(
          `${this.getApplicationUrl(this.uri)}/${applicationId}`,
          { applicationStatusId: statusId, _version },
          { headers }
        )
        .pipe(
          map(mapApplicationDtoToModel),
          catchError((error: HttpErrorResponse) =>
            of({ error, applicationId, applicationFullName })
          ),
          finalize(() =>
            // Update currentCount on both success and failure
            currentCount?.update((previousValue) => previousValue + 1)
          )
        )
    );
  }

  getDeleteCalls({
    applications,
    currentCount,
  }: DeleteCallsPayload): Observable<Application | BatchError>[] {
    return applications.map((application) =>
      this.delete(application.id, getOptionalContext()).pipe(
        map(() => application),
        catchError((error: HttpErrorResponse) =>
          of({
            error,
            applicationId: application.id,
            applicationFullName: getApplicationFullName(application),
          })
        ),
        finalize(() =>
          // Update currentCount on both success and failure
          currentCount?.update((previousValue) => previousValue + 1)
        )
      )
    );
  }

  getApplicationCount(vacancyId: string, accountId: string): Observable<number> {
    return this.getWithQuery({
      params: {
        filter: `vacancy.id==${vacancyId}`,
        limit: 0,
      },
      withAccountId: accountId,
      context: getOptionalContext(),
      headers: { maxRetry: 1 },
    }).pipe(map(({ totalCount }) => totalCount));
  }

  private fetchApplicationCountByStatus({
    statusId,
    vacancyId,
    accountId,
  }: FetchApplicationCountPayload): Observable<number> {
    const params: QueryParams = {
      filter: getFilter({ statusId, vacancyId }),
      offset: 0,
      limit: 0,
    };

    return this.http
      .get<CoreListEnvelop<ApplicationDto>>(
        this.getApplicationUrl(`account/${accountId}/${this.uri}`),
        { params }
      )
      .pipe(
        map((applicationsResponse) => applicationsResponse.totalCount),
        catchError(() => of(0))
      );
  }
}

const getFilter = ({
  statusId,
  vacancyId,
}: Pick<FetchApplicationPayload, 'statusId' | 'vacancyId'>): string =>
  buildQueryString([
    statusId ? `applicationStatus.id==${statusId}` : undefined,
    `vacancy.id==${vacancyId}`,
  ]);

const getSearchFilter = ({ vacancyId, type, text }: Omit<SearchApplicationsPayload, 'accountId'>) =>
  buildQueryString([`${type}=@${escapeDelimiters(text)}`, `vacancy.id==${vacancyId}`]);

const buildQueryString = (filterElements: (string | undefined)[]) =>
  filterElements.filter(Boolean).join(';');

const getFilterByIds = (ids: string[]): string => ids.map((id) => `id==${id}`).join(',');
